import type { AppColumn } from '../../components/AppDataGrid';
import type { ReagentPrepComponent } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// The child grid on each header form — one column set per category, kept here
// beside prepForms.ts so "which grid for which category" has one home.
//
//   Chemistry   → ReagentPreparation_ReagentPrepLogs_ListView             (Model.xafml :38620)
//   Micro Media → ReagentPreparation_ReagentPrepLogs_ListView_MicroMedia  (Model.xafml :38711)
//   Calibration → ReagentPreparation_ReagentPrepLogs_ListView_Calibration (Model.xafml :38674)
//
// Chemistry and Micro Media are read-only in XAF too: components are entered
// through the Prep Notepad. The Calibration levels grid is NOT — it is
// AllowEdit="True" InlineEditMode="Batch", so the detail page makes it
// editable from LEVEL_EDIT below.
//
// HOW THE ORDER IS DERIVED (re-verified 2026-10-16, no ModelDifference rows in
// the database): XAF numbers the VISIBLE members of ReagentPrepLog.cs in
// declaration order — memo (Size(Unlimited)) and VisibleInListView(false)
// members excluded — then applies the model's Index overrides; equal indexes
// keep the model node order. A column with no model Caption shows the BO
// XafDisplayName (LT#, LLT#, Autoclave(Y/N)) or else XAF's split member name.
// ─────────────────────────────────────────────────────────────────────────────

/** Chemistry. ComponentID is Index="-1" here, i.e. HIDDEN — the earlier "ID"
 *  first column was taken from the spec and was wrong. Formula and Memo (memo
 *  fields, hidden by default) and VolTakenUnits / WtTakenUnits /
 *  InitialWeightTakenUnits (hidden on the BO) are all re-shown by an explicit
 *  Index; the two Cal_ unit columns are always empty on a Chemistry row, and XAF
 *  shows them anyway. Captions keep the model's trailing spaces. Widths are the
 *  model's. The COA link column is the PreviewCOA action, not yet built. */
export const CHEM_COLS: AppColumn<ReagentPrepComponent>[] = [
  { field: 'vendorStockName',         header: 'VendorStock',              width: 96 },
  { field: 'ltText',                  header: 'LT#',                      width: 96 },
  { field: 'labStockName',            header: 'LabStock',                 width: 96 },
  { field: 'lltText',                 header: 'LLT#',                     width: 96 },
  { field: 'calculationApproachName', header: 'CalculationApproach',      width: 96 },
  { field: 'finalVol_V2',             header: 'FinalVol(V2) ',            width: 96 },
  { field: 'finalConc_C2',            header: 'FinalConc(C2)',            width: 96 },
  { field: 'calV1UnitsName',          header: 'VolTakenUnits',            width: 82 },
  { field: 'v2UnitsName',             header: 'FinalVol(V2)Units',        width: 96 },
  { field: 'calV2UnitsName',          header: 'WtTakenUnits',             width: 82 },
  { field: 'constant',                header: 'Constant',                 width: 96 },
  { field: 'purity',                  header: 'Purity(%)',                width: 96 },
  { field: 'density',                 header: 'Density',                  width: 96 },
  { field: 'formula',                 header: 'Formula',                  width: 96 },
  { field: 'stockConc_C1',            header: 'StockConc(C1) ',           width: 96 },
  { field: 'volumeTaken_V1',          header: 'VolumeTaken(V1) ',         width: 142 },
  { field: 'weight_g_w1',             header: 'Weight(g)(W1)',            width: 96 },
  // A tick box, as the live G5 grid draws this boolean (owner screenshot 2026-11-03).
  { field: 'solvent',                 header: 'Solvent',                  width: 68, align: 'center',
    text: (r) => (r.solvent ? 'True' : 'False'),
    render: (r) => <input type="checkbox" checked={!!r.solvent} disabled readOnly aria-label="Solvent" /> },
  { field: 'comment',                 header: 'Memo',                     width: 96 },
  { field: 'v1UnitsName',             header: 'InitialVolTaken(V1)Units', width: 162 },
  { field: 'c1UnitsName',             header: 'StockConc(C1)Units',       width: 96 },
  { field: 'c2UnitsName',             header: 'FinalConc(C2)Units',       width: 96 },
  // Captions as the live G5 grid shows them (owner screenshot 2026-11-03; was "LT Exp Date").
  { field: 'ltExpDate',               header: 'LTExpDate',                width: 96, type: 'date' },
  { field: 'lltExpDate',              header: 'LLTExpDate',               width: 96, type: 'date' },
  { field: 'w1UnitsName',             header: 'InitialWeightTakenUnits',  width: 82 },
];

/**
 * Chemistry WITH XAF's "COA" column — the in-row PreviewCOA_Reagent action
 * (caption "Attachment"), which XAF shows on the Chemistry components grid only.
 * Clicking it previews the component's LT vendor certificate, else its LLT
 * notebook.
 *
 * Matches the LIVE G5 grid (owner request 2026-11-03, screenshot side by side):
 * VendorStock · LT# · COA · LabStock · LLT# · CalculationApproach · … — COA right
 * AFTER LT#, and NO Component ID column (ComponentID is Index="-1" in the model).
 * This reverses the 2026-10-23 request, which led with Component ID and put COA
 * just before CalculationApproach.
 */
export function chemColumnsWithCoa(
  onCoa: (row: ReagentPrepComponent) => void,
  /** False on a NEW, unsaved prep: its components are not stored yet, so there is
   *  no COA to open and the cell stays empty rather than offering a link that does
   *  nothing (owner bug list #5, 2026-11-03). */
  canPreview = true,
): AppColumn<ReagentPrepComponent>[] {
  const coa: AppColumn<ReagentPrepComponent> = {
    field: 'oid', header: 'COA', width: 90,
    text: () => (canPreview ? 'Attachment' : ''),
    render: (row) => (canPreview ? (
      <button type="button" className="rgp-link"
              onClick={(e) => { e.stopPropagation(); onCoa(row); }}>
        Attachment
      </button>
    ) : null),
  };
  const at = CHEM_COLS.findIndex((c) => c.field === 'ltText') + 1;
  return [...CHEM_COLS.slice(0, at), coa, ...CHEM_COLS.slice(at)];
}

/** Micro Media — order and widths are the model's (every column has an Index). */
export const MICRO_COLS: AppColumn<ReagentPrepComponent>[] = [
  { field: 'componentID',         header: 'Component ID',             width: 82 },
  { field: 'vendorStockName',     header: 'VendorStock',              width: 82 },
  { field: 'ltText',              header: 'LT#',                      width: 82 },
  { field: 'labStockName',        header: 'Lab Stock',                width: 82 },
  { field: 'lltText',             header: 'LLT#',                     width: 82 },
  { field: 'weight_g_w1',         header: 'WtTaken',                  width: 82 },
  { field: 'volumeTaken_V1',      header: 'VolTaken',                 width: 82 },
  { field: 'finalVol_V2',         header: 'FinalVol',                 width: 82 },
  { field: 'solvent',             header: 'Solvent',                  width: 58 },
  { field: 'phCriteria',          header: 'PH Criteria',              width: 70 },
  { field: 'ph',                  header: 'PH',                       width: 70 },
  { field: 'autoclave',           header: 'Autoclave(Y/N)',           width: 70 },
  { field: 'filterSterilization', header: 'FilterSterilization(Y/N)', width: 70 },
  { field: 'sporeGrowth',         header: 'SporeGrowth(Y/N)',         width: 70 },
  { field: 'positiveControl',     header: '+Control',                 width: 70 },
  { field: 'negativeControl',     header: '-Control',                 width: 70 },
];

/** Calibration levels. Captions and order are the model's; WorkingStdName and
 *  StockStdName are 200 wide because the controller widens them (ctrl :4196). */
export const LEVEL_COLS: AppColumn<ReagentPrepComponent>[] = [
  { field: 'componentID',        header: 'Level',          width: 40 },
  { field: 'workingStdName',     header: 'WorkingStdName', width: 200 },
  { field: 'stockStdName',       header: 'StockStdName',   width: 200 },
  { field: 'cal_Weight_g_w1',    header: 'WSconc',         width: 70 },
  { field: 'calWsUnitsName',     header: 'Units',          width: 70 },
  { field: 'cal_VolumeTaken_V1', header: 'VolTaken',       width: 70 },
  { field: 'calV1UnitsName',     header: 'VolTaken_Unit',  width: 110 },
  { field: 'cal_FinalVol_V2',    header: 'FinalVol',       width: 70 },
  { field: 'calV2UnitsName',     header: 'FinalVol_Unit',  width: 110 },
  { field: 'cal_FinalConc_C2',   header: 'FinalConc',      width: 80 },
  { field: 'calC2UnitsName',     header: 'FinalConc_Unit', width: 120 },
  { field: 'formula',            header: 'Formula',        width: 100 },
  { field: 'comment',            header: 'Comment',        width: 100 },
  { field: 'subLLT',             header: 'SubLLT',         width: 150 },
];

/** How each levels-grid column is edited (XAF batch edit).
 *   text  — typed in place
 *   unit  — a ReagentUnits combo; `idKey` is the Id the save sends
 *   pick  — StockStdName: AllowEdit="False", filled ONLY by double-clicking it
 *           (ctrl :5263), which opens the StockStdName picker
 *  Level is deliberately not editable: renumbering one would duplicate its
 *  SubLLT. SubLLT is computed. Both are absent here, so they stay read-only. */
export const LEVEL_EDIT: Partial<Record<keyof ReagentPrepComponent,
  { kind: 'text' } | { kind: 'unit'; idKey: keyof ReagentPrepComponent } | { kind: 'pick' }>> = {
  workingStdName:     { kind: 'text' },
  stockStdName:       { kind: 'pick' },
  cal_Weight_g_w1:    { kind: 'text' },
  calWsUnitsName:     { kind: 'unit', idKey: 'calWsUnitsId' },
  cal_VolumeTaken_V1: { kind: 'text' },
  calV1UnitsName:     { kind: 'unit', idKey: 'calV1UnitsId' },
  cal_FinalVol_V2:    { kind: 'text' },
  calV2UnitsName:     { kind: 'unit', idKey: 'calV2UnitsId' },
  cal_FinalConc_C2:   { kind: 'text' },
  calC2UnitsName:     { kind: 'unit', idKey: 'calC2UnitsId' },
  formula:            { kind: 'text' },
  comment:            { kind: 'text' },
};
